import {
    MediaProgram,
    WorkflowStepDetail,
    WorkflowStepInfo,
    WorkflowStepType,
    WorkflowWorkflowInfo,
} from '@calm/evexia-web-sdk';
import programMessages from '@rhg/lang/messages/program';
import { logger } from '@rhg/libs/logger';
import { useCallback, useEffect, useState, MouseEvent } from 'react';
import { useIntl } from 'react-intl';
import { useJoinProgramUi } from '../use-join-program-ui';
import { useSnackbar } from '../use-snackbars';
import { useWorkflowStepDialog } from '../use-step-dialog';
import { useLeaveWorkflow } from '../use-update-workflow';

interface Props {
    program: MediaProgram | undefined;
    workflow: WorkflowWorkflowInfo | undefined;
    currentWorkflowStep: WorkflowStepInfo | undefined;
    packClass: string | undefined;
}

export function useProgramWorkflowActions({
    program,
    workflow,
    currentWorkflowStep,
    packClass,
}: Props) {
    const { formatMessage } = useIntl();
    const { showSnackbar } = useSnackbar();
    const { joinProgram, isPending: isJoinPending } = useJoinProgramUi({
        program,
        workflow,
        programType: program?.isClinical ? 'clinical_program' : undefined,
        packClass,
    });
    const { leaveWorkflow, isPending: isLeavePending } = useLeaveWorkflow();
    const { showConfirmDialog, showPollDialog } = useWorkflowStepDialog({
        workflow,
    });
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [getStartedDialogState, setGetStartedDialogState] = useState({
        isOpen: false,
        justJoined: false,
    });
    const [firedCurrentStepDialog, setFiredCurrentStepDialog] = useState(false);

    const openMenu = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => setMenuAnchor(e.currentTarget),
        []
    );
    const closeMenu = useCallback(() => setMenuAnchor(null), []);

    const handleGetStartedClosed = useCallback(() => {
        setGetStartedDialogState({ isOpen: false, justJoined: false });
    }, [setGetStartedDialogState]);

    const showHowToGetStartedDialog = useCallback(
        (justJoined = false) => {
            closeMenu();
            setGetStartedDialogState({ isOpen: true, justJoined });
        },
        [closeMenu, setGetStartedDialogState]
    );

    const handleStep = useCallback(
        (
            step?: WorkflowStepInfo | WorkflowStepDetail,
            options?: {
                isInStepFlow?: boolean;
                isHandlingJoin?: boolean;
                willSkipTypes?: WorkflowStepType[];
            }
        ) => {
            if (
                step?.stepDefn?.type === 'confirm' &&
                !options?.willSkipTypes?.includes('confirm')
            ) {
                showConfirmDialog(step, {
                    onSuccess: (
                        { data: nextStep },
                        { stepDefnId: previousStepId }
                    ) => {
                        if (nextStep?.stepDefn?.defnId !== previousStepId) {
                            handleStep(nextStep, {
                                isInStepFlow: true,
                                isHandlingJoin: options?.isHandlingJoin,
                            });
                        }
                    },
                    onError: () => {
                        showSnackbar({
                            severity: 'error',
                            message: formatMessage(
                                programMessages['program.join_program_error']
                            ),
                        });
                    },
                });
            } else if (
                step?.stepDefn?.type === 'poll' &&
                !options?.willSkipTypes?.includes('poll')
            ) {
                // TODO when we implement polls
                showPollDialog(step, {
                    onSuccess: () => {
                        handleStep(undefined, {
                            isInStepFlow: true,
                            isHandlingJoin: options?.isHandlingJoin,
                        });
                    },
                });
            } else if (options?.isHandlingJoin) {
                showHowToGetStartedDialog(true);
            }
        },
        [
            showConfirmDialog,
            showHowToGetStartedDialog,
            showPollDialog,
            showSnackbar,
            formatMessage,
        ]
    );

    const onJoinProgram = useCallback(
        async (props?: {
            onSuccess?: () => void;
            willResetProgress: boolean;
        }) => {
            if (isJoinPending) return;
            closeMenu();
            setFiredCurrentStepDialog(true);
            joinProgram(!!props?.willResetProgress, {
                onSuccess: (data) => {
                    handleStep(data, {
                        isInStepFlow: false,
                        isHandlingJoin: true,
                    });
                    props?.onSuccess?.();
                },
                onError: () => {
                    showSnackbar({
                        severity: 'error',
                        message: formatMessage(
                            programMessages['program.join_program_error']
                        ),
                    });
                },
            });
        },
        [
            isJoinPending,
            closeMenu,
            joinProgram,
            handleStep,
            showSnackbar,
            formatMessage,
        ]
    );

    const onLeaveProgram = useCallback(
        async (props?: { onSuccess?: () => void }) => {
            if (isLeavePending) return;
            closeMenu();

            if (!workflow) {
                const err = new Error('no workflow when leaving program');
                logger.error(
                    'no workflow when leaving program',
                    { programId: program?.id },
                    err
                );
                showSnackbar({
                    severity: 'error',
                    message: formatMessage(
                        programMessages['program.leave_program_error']
                    ),
                });
            }
            leaveWorkflow(
                {
                    workflowDefnId: workflow?.workflowDefn?.defnId,
                    workflowType: workflow?.workflowDefn?.type,
                },
                {
                    onSuccess: props?.onSuccess,
                    onError: () => {
                        showSnackbar({
                            severity: 'error',
                            message: formatMessage(
                                programMessages['program.leave_program_error']
                            ),
                        });
                    },
                }
            );
        },
        [
            isLeavePending,
            closeMenu,
            workflow,
            leaveWorkflow,
            program?.id,
            showSnackbar,
            formatMessage,
        ]
    );

    const onRestartProgram = useCallback(
        (props?: { onSuccess?: () => void }) => {
            onJoinProgram({
                onSuccess: props?.onSuccess,
                willResetProgress: true,
            });
        },
        [onJoinProgram]
    );

    useEffect(() => {
        if (currentWorkflowStep && !firedCurrentStepDialog) {
            setFiredCurrentStepDialog(true);
            handleStep(currentWorkflowStep, { willSkipTypes: ['poll'] });
        }
    }, [currentWorkflowStep, firedCurrentStepDialog, handleStep]);

    return {
        menuAnchor,
        getStartedDialogState,
        openMenu,
        closeMenu,
        handleGetStartedClosed,
        showHowToGetStartedDialog,
        onJoinProgram,
        onLeaveProgram,
        onRestartProgram,
        isLeavePending,
        isJoinPending,
        isRestartPending: isJoinPending,
    };
}
